import { useState } from 'react'

import {
	Button, // CircularProgress,
	// Paper,
	TextField,
	Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import PromptBox from 'components/PromptBoxV2/PromptBox'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import editIcon from 'assets/images/editIcon.svg'
import refreshIcon from 'assets/images/refreshIcon.svg'

// import { commonStyles } from 'styles/common.styles'
import { OppositeDayPromptResponse } from '../../../../models/ideaModels'
import { styles } from './Section2.styles'

export const TEST_ID = 'opposite-day-section2'

export type Props = {
	loading: boolean
	oppositeDayPromptResponse?: OppositeDayPromptResponse
	prompt: string // do we need this?
	reloadIdeas: (newPrompt?: string) => void
}

const buildResponseItem = (solution: string) => {
	if (!solution || solution.trim() === '') {
		return (
			<Box sx={styles.exampleIdeasBox}>
				<Typography variant="body1" style={styles.listItem}>
					No ideas generated yet. Click the refresh button to generate
					ideas.
				</Typography>
			</Box>
		)
	}

	try {
		const stringSol = JSON.parse(solution)
		return (
			<Box sx={styles.exampleIdeasBox}>
				<ol data-testid="gen-ai-response-list">
					{stringSol.map((el: string, i: number) => (
						<Typography
							fontSize={16}
							component="li"
							key={`ai-solution-${i}`}
							variant="body1"
							style={styles.listItem}
						>
							{el}
						</Typography>
					))}
				</ol>
			</Box>
		)
	} catch (error) {
		return (
			<Box sx={styles.exampleIdeasBox}>
				<Typography variant="body1" style={styles.listItem}>
					Error loading ideas. Please try refreshing.
				</Typography>
			</Box>
		)
	}
}

const Section2 = (props: Props) => {
	const [editPromptMode, setEditPromptMode] = useState(false)
	const [isGenAiLoading, setIsGenAiLoading] = useState(false)

	const onClickRefresh = () => {
		setIsGenAiLoading(true)
		// Call without parameter for refresh (will generate new prompt)
		props.reloadIdeas()
		setTimeout(() => {
			setIsGenAiLoading(false)
		}, 1000)
	}

	const onClickSaveEditPrompt = () => {
		const textareaPrompt = document.getElementsByTagName('textarea')

		if (textareaPrompt && textareaPrompt.length > 0) {
			setEditPromptMode(false)
			// Pass the updated prompt value to reloadIdeas
			const newPromptValue = textareaPrompt[0].value
			props.reloadIdeas(newPromptValue)
		}
	}

	const handleCancelButtonClick = () => {
		setEditPromptMode(false)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box style={styles.mainBox}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.typographyBox}>
					<Typography
						sx={styles.typography}
						data-testid={`${TEST_ID}-user-directions-verbiage`}
					>
						Consider these intentionally terrible ideas and how the
						opposite of - or perhaps an iteration of - these
						concepts could lead to novelty.
					</Typography>
					<Box sx={styles.refreshButtonBox}>
						<ButtonWithIconComponent
							disabled={props.loading || isGenAiLoading}
							iconSvg={refreshIcon}
							text="REFRESH"
							data-testid={`${TEST_ID}__refresh-button`}
							onClickAction={onClickRefresh}
						/>
					</Box>
					{editPromptMode ? (
						<>
							<TextField
								data-testid={`${TEST_ID}__edit-company-disruption`}
								defaultValue={
									props.oppositeDayPromptResponse?.data
										?.facilitationTechnique ||
									'Generate Scenario Above'
								}
								multiline
								fullWidth
								inputRef={(input) => input?.focus()}
							/>

							<Box sx={styles.saveButtonBox}>
								<Button
									color="secondary"
									variant="contained"
									data-testid={`${TEST_ID}__edit-save-button`}
									onClick={async () => {
										setIsGenAiLoading(true)
										onClickSaveEditPrompt()
										setIsGenAiLoading(false)
									}}
								>
									<Typography>SAVE CHANGES</Typography>
								</Button>
								<Button
									variant="text"
									color="secondary"
									data-testid={`${TEST_ID}__edit-cancel-button`}
									onClick={() => handleCancelButtonClick()}
								>
									<Typography>CANCEL</Typography>
								</Button>
							</Box>
						</>
					) : (
						<>
							<PromptBox
								gameTypeEnum={GAME_TYPE.OPPOSITE_DAY}
								contentText={
									props.oppositeDayPromptResponse?.data
										?.facilitationTechnique ||
									'Generate Scenario Above'
								}
								data-testid={`${TEST_ID}__company-disruption`}
								loading={props.loading}
								hidePromptContainerStyle={false}
								scrollable={true}
							/>
							<Box sx={styles.editButtonBox}>
								<ButtonWithIconComponent
									disabled={props.loading}
									iconSvg={editIcon}
									text="EDIT"
									data-testid={`${TEST_ID}-edit-prompt`}
									onClickAction={() =>
										setEditPromptMode(!editPromptMode)
									}
								/>
							</Box>
						</>
					)}
					<Typography
						sx={styles.typography}
						data-testid={`${TEST_ID}-genai-idea-dropdown`}
					>
						{brainstormText.global.ideasDropdown}
					</Typography>
					<GeneratedExampleDropdown
						gameTypeEnum={GAME_TYPE.OPPOSITE_DAY}
						headerText={''}
						isLoading={isGenAiLoading}
						customResponse={buildResponseItem(
							props.oppositeDayPromptResponse?.data
								?.exampleIdeas ?? ''
						)}
					/>
				</Box>
			</Box>
		</>
	)
}

export default Section2
